import ProjectDataTypes from "../../enums/ProjectDataTypes";
import TrackingDataStatusEnum from "../../enums/TrackingDataStatus";
import { boolOrIntCodec, timestampCodec } from "../../validation/codecs";
import { enumCodec, mixed, optional, required, t } from "../../validation/validator";
import { CallResponseMixinSchema, CallResponseSchema } from "../common/callResponseSchema";
import { ScenarioResponseSchema } from "../scenarios/scenarioSchemas";

/****************************************************
 * Common Fields
 */
const commonFields = {
    name: t.string,
    description: t.union([t.string, t.undefined, t.null]),
    comment: t.union([t.string, t.undefined, t.null]),
    archived: t.union([boolOrIntCodec, t.undefined])
};

/****************************************************
 * Import Formats
 */
export const ImportFormatResponseSchema = t.type({
    id: t.number,
    data_type: enumCodec(ProjectDataTypes),
    name: t.string,
    display_name: t.string,
    description: t.union([t.string, t.null]),
    is_active: t.boolean,
    created_at: timestampCodec,
    updated_at: t.union([timestampCodec, t.null]),
    transformer_name: t.string,
    external_team_id: t.union([t.string, t.null])
});

/****************************************************
 * Tracking Data Types
 */
export const TrackingDataTypeSchema = enumCodec(ProjectDataTypes);

export const TrackingDataStatusSchema = enumCodec(TrackingDataStatusEnum);

/****************************************************
 * Tracking Data Response
 */
export const ProjectTrackingDataResponseSchema = t.type({
    id: t.number,
    project_id: t.number,
    import_format_id: t.number,

    // File storage info
    storage_bucket: t.string,
    storage_base_path: t.string, // Base path without filename

    // Original file info
    original_filename: t.string, // Original filename from user upload
    original_file_size: t.union([t.number, t.null]),

    // Normalized file info
    normalized_filename: t.union([t.string, t.null]), // Processed filename (usually .json)
    normalized_file_size: t.union([t.number, t.null]),

    status: TrackingDataStatusSchema,
    metadata: t.union([t.string, t.null]), // JSON string
    processing_log: t.union([t.string, t.null]), // Processing log
    created_at: timestampCodec,
    updated_at: t.union([timestampCodec, t.null]),
    uploaded_at: t.union([timestampCodec, t.null]),
    processed_at: t.union([timestampCodec, t.null])
});

/****************************************************
 * Folder Scenario (Scenario with clip metadata)
 */
export const FolderScenarioResponseSchema = t.intersection([
    ScenarioResponseSchema,
    t.partial({
        start_time: t.union([t.number, t.null]),
        end_time: t.union([t.number, t.null]),
        period_1_event_offset: t.union([t.number, t.null]),
        period_2_event_offset: t.union([t.number, t.null])
    })
]);

/****************************************************
 * Folder
 */
export const FolderResponseSchema = t.type({
    id: t.number,
    created_at: timestampCodec,
    updated_at: t.union([timestampCodec, t.undefined, t.null]),
    ...commonFields,
    scenarios: t.array(FolderScenarioResponseSchema),
    project_ids: t.union([t.array(t.number), t.undefined])
});

/****************************************************
 * Project
 */
export const ProjectDataFileStatusResponseSchema = t.type({
    id: t.number,
    project_id: t.number,
    status: TrackingDataStatusSchema,
    updated_at: timestampCodec,
    data_type: TrackingDataTypeSchema,
    transformer_name: t.string,
    external_team_id: t.union([t.string, t.null]),
    stream_name: t.string
});

export const ProjectResponseSchema = t.type({
    id: t.number,
    created_at: timestampCodec,
    updated_at: t.union([timestampCodec, t.undefined, t.null]),
    ...commonFields,
    period_1_event_offset: t.union([t.number, t.null, t.undefined]),
    period_2_event_offset: t.union([t.number, t.null, t.undefined]),
    folders: t.array(FolderResponseSchema),
    trackingData: t.array(ProjectDataFileStatusResponseSchema),
    metaData: t.array(ProjectDataFileStatusResponseSchema),
    eventData: t.array(ProjectDataFileStatusResponseSchema),
    skeletalData: t.array(ProjectDataFileStatusResponseSchema)
});

/****************************************************
 * CreateProject
 */
export const CreateProjectSchema = t.intersection([
    t.type({
        name: t.string,
        account_id: t.number,
    }),
    t.partial({
        description: t.union([t.string, t.undefined, t.null]),
        comment: t.union([t.string, t.undefined, t.null]),
        folder_ids: t.union([t.array(t.number), t.undefined, t.null]),
        period_1_event_offset: t.union([t.number, t.undefined]),
        period_2_event_offset: t.union([t.number, t.undefined])
    })
]);

/****************************************************
 * UpdateProject
 */
export const UpdateProjectSchema = t.intersection([
    t.type({
        id: t.number,
        account_id: t.number,
    }),
    t.partial({
        ...commonFields,
        folder_ids: t.array(t.number),
        period_1_event_offset: t.union([t.number, t.undefined]),
        period_2_event_offset: t.union([t.number, t.undefined])
    })
]);

/****************************************************
 * GetProjects
 */
export const GetProjectsSchema = mixed([
    required({
        account_id: t.number,
    }),
    optional({
        ids: t.union([t.array(t.number), t.undefined]),
        include_archived: t.union([t.boolean, t.undefined]),
        limit: t.union([t.number, t.undefined]),
        order_by: t.union([t.array(t.type({
            field: t.string,
            direction: t.union([t.literal('asc'), t.literal('desc')])
        })), t.undefined])
    })
]);

/****************************************************
 * Clip Data Schema
 */
export const ClipDataSchema = t.type({
    scenario_id: t.number,
    start_time: t.union([t.number, t.undefined]),
    end_time: t.union([t.number, t.undefined]),
    period_1_event_offset: t.union([t.number, t.null, t.undefined]),
    period_2_event_offset: t.union([t.number, t.null, t.undefined])
});

/****************************************************
 * CreateFolder
 */
export const CreateFolderSchema = t.intersection([
    t.type({
        name: t.string,
        account_id: t.number,
    }),
    t.partial({
        project_ids: t.array(t.number),
        description: t.string,
        comment: t.string,
        scenario_ids: t.array(t.number),
        scenario_clips: t.array(ClipDataSchema)
    })
]);

/****************************************************
 * UpdateFolder
 */
export const UpdateFolderSchema = t.intersection([
    t.type({
        id: t.number,
        account_id: t.number,
    }),
    t.partial({
        ...commonFields,
        scenario_ids: t.array(t.number),
        scenario_clips: t.array(ClipDataSchema),
        scenario_action: t.union([t.literal('add'), t.literal('remove'), t.literal('replace'), t.undefined])
    })
]);

/****************************************************
 * GetFolders
 */
export const GetFoldersSchema = mixed([
    required({
        account_id: t.number,
    }),
    optional({
        ids: t.union([t.array(t.number), t.undefined]),
        project_ids: t.union([t.array(t.number), t.undefined]),
        include_archived: t.union([t.boolean, t.undefined]),
        limit: t.union([t.number, t.undefined]),
        order_by: t.union([t.array(t.type({
            field: t.string,
            direction: t.union([t.literal('asc'), t.literal('desc')])
        })), t.undefined])
    })
]);

/****************************************************
 * DeleteProject
 */
export const DeleteProjectSchema = t.type({
    id: t.number,
    account_id: t.number,
});

/****************************************************
 * DeleteFolder
 */
export const DeleteFolderSchema = t.type({
    id: t.number,
    account_id: t.number,
});

/****************************************************
 * Add Project to Folder
 */
export const AddProjectToFolderSchema = t.type({
    folder_id: t.number,
    project_id: t.number,
    account_id: t.number,
});

/****************************************************
 * Remove Project from Folder
 */
export const RemoveProjectFromFolderSchema = t.type({
    folder_id: t.number,
    project_id: t.number,
    account_id: t.number,
});

/****************************************************
 * Get Import Formats
 */
export const GetImportFormatsSchema = t.type({
    account_id: t.number,
    data_type: t.union([TrackingDataTypeSchema, t.undefined])
});

/****************************************************
 * Upload Tracking Data
 */
export const UploadTrackingDataSchema = t.type({
    project_id: t.number,
    account_id: t.number,
    data_type: TrackingDataTypeSchema,
    import_format_id: t.number, // Must be enabled for the account
    filename: t.string,
    content_type: t.union([t.string, t.undefined])
});

export const UpdateTrackingDataStatusSchema = t.intersection([
    t.type({
        account_id: t.number,
        data_type: enumCodec(ProjectDataTypes),
        record_id: t.number,
        status: enumCodec(TrackingDataStatusEnum)
    }),
    t.partial({
        file_size: t.union([t.number, t.undefined]),
        metadata: t.union([t.string, t.undefined]), // JSON string
        no_processing: t.union([t.boolean, t.undefined])
    })
]);

export const ProcessTrackingDataSchema = t.type({
    project_id: t.number,
    account_id: t.number
});

/****************************************************
 * Import Match
 */
export const ImportMatchSchema = t.type({
    project_id: t.number,
    account_id: t.number,
    match_id: t.string,
    tracking_provider_id: t.number
});

/****************************************************
 * Get Tracking Data
 */
export const GetTrackingDataSchema = t.intersection([
    t.type({
        project_id: t.number,
        account_id: t.number,
    }),
    t.partial({
        record_id: t.number,
        data_type: TrackingDataTypeSchema,
        status: TrackingDataStatusSchema,
        import_format_id: t.number
    })
]);

/****************************************************
 * Download Tracking Data
 */
export const DownloadTrackingDataSchema = t.type({
    project_id: t.number,
    account_id: t.number,
    data_type: TrackingDataTypeSchema,
    record_id: t.number,
    file_type: t.union([t.literal('original'), t.literal('normalized')])
});

// Types
export type TrackingDataType = t.TypeOf<typeof TrackingDataTypeSchema>;
export type TrackingDataStatus = t.TypeOf<typeof TrackingDataStatusSchema>;
export type ImportFormatResponse = t.TypeOf<typeof ImportFormatResponseSchema>;
export type ProjectTrackingDataResponse = t.TypeOf<typeof ProjectTrackingDataResponseSchema>;
export type ProjectResponse = t.TypeOf<typeof ProjectResponseSchema>;
export type FolderResponse = t.TypeOf<typeof FolderResponseSchema>;
export type CreateProjectRequest = t.TypeOf<typeof CreateProjectSchema>;
export type UpdateProjectRequest = t.TypeOf<typeof UpdateProjectSchema>;
export type GetProjectsRequest = t.TypeOf<typeof GetProjectsSchema>;
export type CreateFolderRequest = t.TypeOf<typeof CreateFolderSchema>;
export type UpdateFolderRequest = t.TypeOf<typeof UpdateFolderSchema>;
export type GetFoldersRequest = t.TypeOf<typeof GetFoldersSchema>;
export type DeleteProjectRequest = t.TypeOf<typeof DeleteProjectSchema>;
export type DeleteFolderRequest = t.TypeOf<typeof DeleteFolderSchema>;
export type AddProjectToFolderRequest = t.TypeOf<typeof AddProjectToFolderSchema>;
export type RemoveProjectFromFolderRequest = t.TypeOf<typeof RemoveProjectFromFolderSchema>;
export type GetImportFormatsRequest = t.TypeOf<typeof GetImportFormatsSchema>;
export type UploadTrackingDataRequest = t.TypeOf<typeof UploadTrackingDataSchema>;
export type GetTrackingDataRequest = t.TypeOf<typeof GetTrackingDataSchema>;
export type DownloadTrackingDataRequest = t.TypeOf<typeof DownloadTrackingDataSchema>;
export type ProjectDataFileStatusResponse = t.TypeOf<typeof ProjectDataFileStatusResponseSchema>;
export type ClipData = t.TypeOf<typeof ClipDataSchema>;
export type FolderScenarioResponse = t.TypeOf<typeof FolderScenarioResponseSchema>;

// Client API
export const ProjectApis = {
    Projects: {
        CreateProject: {
            Name: "projects-createProject",
            Request: CreateProjectSchema,
            Response: CallResponseSchema,
        },
        UpdateProject: {
            Name: "projects-updateProject",
            Request: UpdateProjectSchema,
            Response: CallResponseSchema,
        },
        GetProjects: {
            Name: "projects-getProjects",
            Request: GetProjectsSchema,
            Response: mixed([required({ data: t.array(ProjectResponseSchema) }), CallResponseMixinSchema]),
        },
        DeleteProject: {
            Name: "projects-deleteProject",
            Request: DeleteProjectSchema,
            Response: CallResponseSchema,
        }
    },
    Folders: {
        CreateFolder: {
            Name: "projects-folders-temp-createFolder",
            Request: CreateFolderSchema,
            Response: CallResponseSchema,
        },
        UpdateFolder: {
            Name: "projects-folders-updateFolder",
            Request: UpdateFolderSchema,
            Response: CallResponseSchema,
        },
        GetFolders: {
            Name: "projects-folders-getFolders",
            Request: GetFoldersSchema,
            Response: mixed([required({ data: t.array(FolderResponseSchema) }), CallResponseMixinSchema]),
        },
        DeleteFolder: {
            Name: "projects-folders-deleteFolder",
            Request: DeleteFolderSchema,
            Response: CallResponseSchema,
        },
        AddProjectToFolder: {
            Name: "projects-folders-addProjectToFolder",
            Request: AddProjectToFolderSchema,
            Response: CallResponseSchema,
        },
        RemoveProjectFromFolder: {
            Name: "projects-folders-removeProjectFromFolder",
            Request: RemoveProjectFromFolderSchema,
            Response: CallResponseSchema,
        }
    },
    ImportFormats: {
        GetImportFormats: {
            Name: "projects-getImportFormats",
            Request: GetImportFormatsSchema,
            Response: mixed([required({ data: t.array(ImportFormatResponseSchema) }), CallResponseMixinSchema]),
        }
    },
    TrackingData: {
        UploadTrackingData: {
            Name: "projects-uploadTrackingData",
            Request: UploadTrackingDataSchema,
            Response: mixed([required({ data: t.type({ 
                upload_url: t.string, 
                file_path: t.string,
                record_id: t.number,
                import_format: ImportFormatResponseSchema
            }) }), CallResponseMixinSchema]),
        },
        GetTrackingData: {
            Name: "projects-getTrackingData",
            Request: GetTrackingDataSchema,
            Response: mixed([required({ data: t.array(ProjectTrackingDataResponseSchema) }), CallResponseMixinSchema]),
        },
        DownloadTrackingData: {
            Name: "projects-downloadTrackingData",
            Request: DownloadTrackingDataSchema,
            Response: mixed([required({ data: t.type({ download_url: t.string }) }), CallResponseMixinSchema]),
        },
        UpdateTrackingDataStatus: {
            Name: "projects-updateTrackingDataStatus",
            Request: UpdateTrackingDataStatusSchema,
            Response: CallResponseSchema,
        },
        ImportMatch: {
            Name: "projects-importMatch",
            Request: ImportMatchSchema,
            Response: CallResponseSchema,
        },
        ProcessTrackingData: {
            Name: "projects-processTrackingData",
            Request: ProcessTrackingDataSchema,
            Response: CallResponseSchema,
        }
    }
}; 